import { css, html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type {
    Commerce,
    CommerceCustomerAction,
    CommerceCustomerSession,
} from "@teqtank/commerce-sdk";

@customElement("teqtank-customer-menu")
export class TeqtankCustomerMenu extends LitElement {
    @property({ attribute: false }) public accessor commerce: Commerce | undefined;
    @state() private accessor session: CommerceCustomerSession | undefined;
    @state() private accessor actions: readonly CommerceCustomerAction[] = [];
    @state() private accessor open = false;
    @state() private accessor busyAction = "";
    @state() private accessor errorMessage = "";
    private unsubscribe: (() => void) | undefined;
    private trigger: HTMLElement | undefined;

    public static override styles = css`
        :host {
            display: inline-flex;
            color: var(--teqtank-color-text, #18221e);
            font-family: var(--teqtank-font-body, Inter, ui-sans-serif, system-ui, sans-serif);
        }
        button {
            font: inherit;
        }
        .trigger {
            position: relative;
            display: grid;
            width: 2.65rem;
            height: 2.65rem;
            place-items: center;
            padding: 0;
            border: 1px solid var(--teqtank-color-border, #ded8cf);
            border-radius: 999px;
            background: var(--teqtank-color-surface, #fff);
            color: inherit;
            cursor: pointer;
        }
        .trigger:hover {
            border-color: currentColor;
        }
        .trigger:focus-visible,
        .close:focus-visible,
        .action:focus-visible,
        .sign-out:focus-visible {
            outline: 3px solid
                color-mix(in srgb, var(--teqtank-color-primary, #315a47) 32%, transparent);
            outline-offset: 2px;
        }
        svg {
            width: 1.35rem;
            height: 1.35rem;
            fill: none;
            stroke: currentColor;
            stroke-width: 1.8;
        }
        .badge {
            position: absolute;
            right: -0.2rem;
            bottom: -0.15rem;
            display: grid;
            min-width: 1.15rem;
            height: 1.15rem;
            place-items: center;
            padding: 0 0.15rem;
            border: 2px solid var(--teqtank-color-surface, #fff);
            border-radius: 999px;
            background: var(--teqtank-color-primary, #315a47);
            color: #fff;
            font-size: 0.52rem;
            font-weight: 800;
            line-height: 1;
        }
        dialog {
            width: min(var(--teqtank-customer-menu-width, 28rem), 100%);
            max-width: none;
            height: 100dvh;
            max-height: none;
            margin: 0 0 0 auto;
            padding: 0;
            border: 0;
            color: inherit;
            background: transparent;
            overflow: hidden;
        }
        dialog::backdrop {
            background: rgb(10 18 14 / 46%);
        }
        .drawer {
            display: flex;
            width: 100%;
            height: 100%;
            flex-direction: column;
            overflow: auto;
            background: var(--teqtank-customer-menu-background, var(--teqtank-color-surface, #fff));
            box-shadow: -16px 0 44px rgb(0 0 0 / 18%);
        }
        .header {
            position: sticky;
            z-index: 1;
            top: 0;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1rem;
            padding: 1rem 1.15rem;
            border-bottom: 1px solid var(--teqtank-color-border, #ded8cf);
            background: inherit;
        }
        h2,
        p {
            margin: 0;
        }
        h2 {
            font-family: var(--teqtank-font-heading, Georgia, serif);
            font-size: 1.35rem;
        }
        .close {
            display: grid;
            width: 2.4rem;
            height: 2.4rem;
            place-items: center;
            border: 0;
            border-radius: 999px;
            background: var(--teqtank-color-surface-soft, #eee8df);
            cursor: pointer;
        }
        .content {
            display: grid;
            gap: 1.15rem;
            padding: 1.15rem;
        }
        .identity {
            display: grid;
            grid-template-columns: auto 1fr;
            align-items: center;
            gap: 0.8rem;
        }
        .avatar {
            display: grid;
            width: 2.75rem;
            height: 2.75rem;
            place-items: center;
            border-radius: 999px;
            background: var(--teqtank-color-primary, #315a47);
            color: #fff;
            font-weight: 800;
        }
        .identity p {
            color: var(--teqtank-color-text-muted, #69736d);
            font-size: 0.85rem;
            overflow-wrap: anywhere;
        }
        .actions {
            display: grid;
            gap: 0.65rem;
        }
        .action,
        .sign-out {
            width: 100%;
            padding: 0.85rem 1rem;
            border: 1px solid var(--teqtank-color-border, #ded8cf);
            border-radius: 0.75rem;
            background: var(--teqtank-color-surface, #fff);
            color: inherit;
            text-align: left;
            cursor: pointer;
        }
        .action {
            display: grid;
            gap: 0.18rem;
        }
        .action strong {
            font-size: 0.92rem;
        }
        .action small {
            color: var(--teqtank-color-text-muted, #69736d);
        }
        .action:hover,
        .sign-out:hover {
            border-color: var(--teqtank-color-primary, #315a47);
        }
        .sign-out {
            text-align: center;
            font-weight: 700;
        }
        button[disabled] {
            cursor: wait;
            opacity: 0.58;
        }
        .error {
            padding: 0.75rem;
            border-radius: 0.65rem;
            background: #fff1f0;
            color: #8c1d18;
            font-size: 0.86rem;
        }
        .empty {
            color: var(--teqtank-color-text-muted, #69736d);
        }
        teqtank-login {
            display: block;
        }
        @media (prefers-reduced-motion: no-preference) {
            .drawer {
                animation: enter 160ms ease-out;
            }
            @keyframes enter {
                from {
                    transform: translateX(1rem);
                    opacity: 0.65;
                }
            }
        }
    `;

    public override connectedCallback(): void {
        super.connectedCallback();
        this.bind();
        window.addEventListener("keydown", this.onKeyDown);
    }
    protected override updated(changed: Map<PropertyKey, unknown>): void {
        if (changed.has("commerce")) this.bind();
    }
    public override disconnectedCallback(): void {
        this.unsubscribe?.();
        window.removeEventListener("keydown", this.onKeyDown);
        super.disconnectedCallback();
    }

    protected override render() {
        const session = this.session ?? this.commerce?.customer.session;
        const authenticated = session?.status === "authenticated";
        return html`
            <button
                class="trigger"
                part="trigger"
                type="button"
                aria-label=${authenticated ? "Open account menu" : "Sign in"}
                aria-expanded=${this.open}
                @click=${this.show}
            >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="12" cy="8" r="4"></circle>
                    <path d="M4.5 21a7.5 7.5 0 0 1 15 0"></path>
                </svg>
                ${authenticated ? html`<span class="badge" part="badge" aria-hidden="true">${session.customer?.initials || "✓"}</span>` : nothing}
            </button>
            ${
                this.open
                    ? html` <dialog
                          part="dialog"
                          aria-labelledby="tt-account-title"
                          @click=${this.onBackdrop}
                          @cancel=${this.onCancel}
                      >
                          <section class="drawer" part="drawer">
                              <header class="header" part="header">
                                  <h2 id="tt-account-title" part="title">
                                      ${authenticated ? "Your account" : "Sign in"}
                                  </h2>
                                  <button
                                      class="close"
                                      part="close"
                                      type="button"
                                      aria-label="Close account menu"
                                      @click=${this.hide}
                                  >
                                      ×
                                  </button>
                              </header>
                              <div class="content" part="content">
                                  ${this.errorMessage ? html`<p class="error" part="error" role="alert">${this.errorMessage}</p>` : nothing}
                                  ${authenticated ? this.renderAuthenticated(session) : html`<teqtank-login part="login" exportparts="card,title,tabs,form,primary-action,secondary-action,error" .commerce=${this.commerce}></teqtank-login>`}
                              </div>
                          </section>
                      </dialog>`
                    : nothing
            }
        `;
    }

    private renderAuthenticated(session: CommerceCustomerSession) {
        const customer = session.customer;
        return html` <div class="identity" part="identity">
                <span class="avatar" part="avatar" aria-hidden="true"
                    >${customer?.initials || "✓"}</span
                >
                <div part="identity-content">
                    <strong part="customer-name">${customer?.displayName ?? "Signed in"}</strong>
                    <p part="customer-email">${customer?.email ?? ""}</p>
                </div>
            </div>
            <nav class="actions" part="actions" aria-label="Account actions">
                ${
                    this.actions.length === 0
                        ? html`<p class="empty" part="empty">
                              No account actions are currently available.
                          </p>`
                        : this.actions.map(
                              (action) =>
                                  html` <button
                                      class="action"
                                      part="action"
                                      type="button"
                                      ?disabled=${this.busyAction !== ""}
                                      @click=${() => this.launch(action.id)}
                                  >
                                      <strong>${action.label}</strong
                                      >${action.description ? html`<small>${action.description}</small>` : nothing}
                                  </button>`,
                          )
                }
            </nav>
            <button
                class="sign-out"
                part="sign-out"
                type="button"
                ?disabled=${this.busyAction !== ""}
                @click=${this.logout}
            >
                Sign out
            </button>`;
    }

    private bind(): void {
        this.unsubscribe?.();
        this.unsubscribe = undefined;
        this.session = this.commerce?.customer.session;
        if (!this.commerce) return;
        this.unsubscribe = this.commerce.customer.onChanged((event) => {
            this.session = event.session;
            this.errorMessage = "";
            this.actions = [];
            if (this.open && event.session.status === "authenticated") void this.loadActions();
        });
    }

    private readonly show = (event: Event): void => {
        this.trigger = event.currentTarget as HTMLElement;
        this.open = true;
        this.errorMessage = "";
        if ((this.session ?? this.commerce?.customer.session)?.status === "authenticated")
            void this.loadActions();
        void this.updateComplete.then(() => {
            const dialog = this.renderRoot.querySelector<HTMLDialogElement>("dialog");
            if (dialog && !dialog.open) dialog.showModal();
            this.renderRoot.querySelector<HTMLElement>(".close")?.focus();
        });
    };
    private readonly hide = (): void => {
        this.renderRoot.querySelector<HTMLDialogElement>("dialog")?.close();
        this.open = false;
        this.trigger?.focus();
    };
    private readonly onBackdrop = (event: MouseEvent): void => {
        if (event.target instanceof HTMLDialogElement) this.hide();
    };
    private readonly onCancel = (event: Event): void => {
        event.preventDefault();
        this.hide();
    };
    private readonly onKeyDown = (event: KeyboardEvent): void => {
        if (this.open && event.key === "Escape") this.hide();
    };

    private async loadActions(): Promise<void> {
        try {
            this.actions = await this.requireCommerce().customer.getActions();
        } catch (error) {
            this.actions = [];
            this.errorMessage =
                error instanceof Error ? error.message : "Account actions could not be loaded.";
        }
    }
    private async launch(actionId: string): Promise<void> {
        this.busyAction = actionId;
        this.errorMessage = "";
        try {
            const destination = new URL(
                await this.requireCommerce().customer.launchAction(actionId),
            );
            if (destination.protocol !== "https:" && destination.protocol !== "http:")
                throw new Error("The account destination is invalid.");
            window.location.assign(destination.href);
        } catch (error) {
            this.errorMessage =
                error instanceof Error ? error.message : "That account action could not be opened.";
        } finally {
            this.busyAction = "";
        }
    }
    private readonly logout = async (): Promise<void> => {
        this.busyAction = "logout";
        this.errorMessage = "";
        try {
            await this.requireCommerce().customer.logout();
        } catch (error) {
            this.errorMessage = error instanceof Error ? error.message : "Sign out failed.";
        } finally {
            this.busyAction = "";
        }
    };
    private requireCommerce(): Commerce {
        if (!this.commerce) throw new Error("Commerce runtime is unavailable.");
        return this.commerce;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "teqtank-customer-menu": TeqtankCustomerMenu;
    }
}
